import { Flex, Heading, Select, Text } from '@applyboard/crystal-ui'
import { DestinationCountryStatus, DocumentTags } from '../../../../utils/enums'
import { FileUploadField } from '../FileUploadField'
import { useApplicationFormContext } from '../ApplicationForm'
import { ComponentType, useState } from 'react'
import { PersonalInformationFormFields } from './types'
import { StudentApplication } from '../../types'
import { Asterisk } from '../../../Asterisk'
import { ALLOWED_STATUS_FILE_TYPES } from './consts'

type StatusSectionProps = Readonly<{
  Field: ComponentType<any>
  application: StudentApplication
  setFieldValues: (data: Partial<PersonalInformationFormFields>) => void
  immigrationStatus: PersonalInformationFormFields['immigrationStatus']
  immigrationFiles: PersonalInformationFormFields['immigrationFiles']
  disabled?: boolean
  isRequestingPassportApi?: boolean
  destinationCountryName: string
}>

export function StatusSection({ Field, ...props }: StatusSectionProps) {
  const [hasValidVisa, setHasValidVisa] = useState(
    props.immigrationStatus !== 'NO_STATUS' && !!props.immigrationStatus,
  )
  const { addPendingDelete, getObservableFiles } = useApplicationFormContext()

  const immigrationStatusList = [
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.NO_STATUS,
      label: "I don't have this",
    },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.STUDY_PERMIT,
      label: 'Study permit',
    },
    { key: crypto.randomUUID(), value: DestinationCountryStatus.WORK_PERMIT, label: 'Work permit' },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.VISITOR,
      label: 'Visitor/Tourist visa',
    },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.OTHER,
      label: 'Other',
    },
  ]

  return (
    <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
      <Flex basis="100%">
        <Heading variant="titleS" level={3}>
          ✈️ Step 3: Provide destination status
        </Heading>
      </Flex>
      <Flex basis="100%" direction="column">
        <Heading variant="titleS" level={3}>
          🌎 {props.destinationCountryName || 'Destination Country'} Status
        </Heading>
        <Text variant="bodyS">
          Tell us below your current visa/immigration status in {props.destinationCountryName}.
        </Text>
      </Flex>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          as={Select}
          label="Status"
          name="immigrationStatus"
          appearance="styled"
          disabled={props.disabled || props.isRequestingPassportApi}
          required={!props.disabled ? 'Status is required' : false}
          onChange={(v: string) => {
            const fileIds = Object.keys(
              getObservableFiles({ fileType: DocumentTags.DESTINATION_COUNTRY_STATUS }),
            )
            if ((!v || v === 'NO_STATUS') && !!fileIds.length) {
              fileIds.forEach(id => addPendingDelete(id))
            }
            setHasValidVisa(!!v && v !== 'NO_STATUS')
          }}
        >
          {immigrationStatusList.map(status => (
            <Select.Option key={status.key} label={status.label} value={status.value} />
          ))}
        </Field>
      </Flex.Item>
      {hasValidVisa ? (
        <Flex gap={4} direction="column" basis="100%">
          <Field
            as={FileUploadField}
            allowedFileTypes={ALLOWED_STATUS_FILE_TYPES}
            application={props.application}
            disabled={props.disabled || !hasValidVisa || props.isRequestingPassportApi}
            fileType={DocumentTags.DESTINATION_COUNTRY_STATUS}
            label={
              <Text variant={'bodyM'}>
                Add your{' '}
                {
                  immigrationStatusList.find(status => status.value === props.immigrationStatus)
                    ?.label
                }{' '}
                document(s) below, supported file formats:{' '}
                {ALLOWED_STATUS_FILE_TYPES.map(v => v.replace('.', '').toLocaleUpperCase()).join(
                  ', ',
                )}{' '}
                <Asterisk />
              </Text>
            }
            name={`immigrationFiles`}
            onRemove={(id: string) =>
              props.setFieldValues({
                immigrationFiles: props.immigrationFiles.filter(file => file.id !== id),
              })
            }
            section={`destinationCountryStatus`}
            validate={(value: string) => {
              const observableFiles = getObservableFiles({
                fileType: DocumentTags.DESTINATION_COUNTRY_STATUS,
                sectionReference: 'destinationCountryStatus',
              })

              if (hasValidVisa && !(Object.keys(observableFiles).length || value.length)) {
                return 'This field is required'
              }
              return true
            }}
            showHistory={false}
          />
        </Flex>
      ) : null}
    </Flex>
  )
}
